import { useState } from "react";
import { Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface CategoryOption {
  id: string;
  label: string;
}

interface BulkActionBarProps {
  selectedCount: number;
  selectedLabel: string;
  changeCategoryLabel: string;
  deleteLabel: string;
  closeLabel: string;
  categoryPlaceholder: string;
  categoryDialogTitle: string;
  applyLabel: string;
  cancelLabel: string;
  categories: CategoryOption[];
  pendingCategoryId: string;
  onPendingCategoryIdChange: (value: string) => void;
  onApplyCategory: () => void;
  onDelete: () => void;
  onClear: () => void;
}

export default function BulkActionBar({
  selectedCount,
  selectedLabel,
  changeCategoryLabel,
  deleteLabel,
  closeLabel,
  categoryPlaceholder,
  categoryDialogTitle,
  applyLabel,
  cancelLabel,
  categories,
  pendingCategoryId,
  onPendingCategoryIdChange,
  onApplyCategory,
  onDelete,
  onClear,
}: BulkActionBarProps) {
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[var(--border-color)] bg-[var(--bg-secondary)] px-[14px] py-[10px] text-[13px]"
      role="status"
      aria-live="polite"
    >
      <div className="text-[13px] font-normal text-[var(--text-primary)]">
        {selectedCount} {selectedLabel}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 rounded-[8px] border-[var(--border-color)] bg-transparent px-3 text-[12px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            >
              {changeCategoryLabel}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm border-[var(--border-color)] bg-[var(--bg-secondary)]">
            <DialogHeader>
              <DialogTitle className="text-[14px] font-medium text-[var(--text-primary)]">
                {categoryDialogTitle}
              </DialogTitle>
            </DialogHeader>
            <Select value={pendingCategoryId} onValueChange={onPendingCategoryIdChange}>
              <SelectTrigger className="h-9 border-[var(--border-color)] bg-[var(--bg-deep)] text-[13px] font-normal text-[var(--text-primary)]">
                <SelectValue placeholder={categoryPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="h-8 rounded-[8px] border-[var(--border-color)] bg-transparent px-3 text-[12px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
                onClick={() => setCategoryDialogOpen(false)}
              >
                {cancelLabel}
              </Button>
              <Button
                type="button"
                className="h-8 rounded-[8px] bg-[var(--fg-accent)] px-3 text-[12px] font-medium text-white hover:bg-[var(--fg-accent-hover)]"
                onClick={() => {
                  onApplyCategory();
                  setCategoryDialogOpen(false);
                }}
                disabled={!pendingCategoryId}
              >
                {applyLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 rounded-[8px] border-[var(--border-color)] bg-transparent px-3 text-[12px] font-medium text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
          onClick={onDelete}
        >
          <Trash2 size={13} className="me-1.5" />
          {deleteLabel}
        </Button>

        <button
          type="button"
          aria-label={closeLabel}
          onClick={onClear}
          className="grid h-[26px] w-[26px] place-items-center rounded-[6px] text-[var(--fg-accent)] transition-colors hover:bg-[var(--bg-deep)] hover:text-[var(--fg-accent-hover)]"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
